import { useSyncExternalStore } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronUp, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skel } from "@/components/app/Common";
import { useAuth } from "@/lib/auth";
import { EASE } from "@/components/motion/Motion";

// Shared hidden/shown state (per user), so the in-header chip and the in-flow bar stay in sync.
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
function useHidden() {
  const { user } = useAuth();
  const key = `tl_nsb_min_${user?.id || "anon"}`;
  const hidden = useSyncExternalStore(subscribe, () => localStorage.getItem(key) === "1");
  const set = (v) => { localStorage.setItem(key, v ? "1" : "0"); listeners.forEach((l) => l()); };
  return [hidden, set];
}

/** Inline chip for the page header row; only shows while the bar is hidden. Never floats. */
export function NextStepChip() {
  const [hidden, setHidden] = useHidden();
  if (!hidden) return null;
  return (
    <Button variant="outline" size="sm" onClick={() => setHidden(false)} data-testid="next-step-pill" className="text-brand">
      <Compass size={14} aria-hidden="true" /> Show next step <ChevronDown size={14} aria-hidden="true" />
    </Button>
  );
}

/** In-flow bar placed under the page header. Takes its own space, so it can't cover content. */
export function NextStepBar({ text, hint, actionLabel, onAction, disabled, loading }) {
  const [hidden, setHidden] = useHidden();
  if (hidden) return null;
  return (
    <motion.section data-testid="next-step-bar" data-tour="next-step" aria-label="Next step"
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22, ease: EASE }}
      className="flex flex-wrap items-center gap-4 rounded-[6px] border border-brand/15 bg-brand-tint/50 px-5 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-brand shadow-card"><Compass size={16} aria-hidden="true" /></div>
      <div className="min-w-[200px] flex-1">
        <p className="text-xs font-medium text-brand">Next step</p>
        {loading ? <Skel className="mt-1 h-4 w-56" /> : <p className="text-sm font-medium text-ink" data-testid="next-step-text">{text}</p>}
        {hint && <p className="text-xs text-ink-secondary">{hint}</p>}
      </div>
      {actionLabel && (
        <Button data-testid="next-step-action-btn" disabled={disabled} onClick={onAction}>{actionLabel} <ArrowRight size={16} /></Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => setHidden(true)} data-testid="next-step-minimise" aria-label="Hide next step" className="text-ink-secondary">
        Hide <ChevronUp size={14} aria-hidden="true" />
      </Button>
    </motion.section>
  );
}
